import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PlayStep } from "../types";
import { interpolateStep } from "../lib/play";

export function usePlayback(steps: PlayStep[]) {
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const lastFrame = useRef<number | null>(null);
  const frame = useRef<number | null>(null);

  const goTo = useCallback((next: number) => {
    setIndex(Math.max(0, Math.min(steps.length - 1, next)));
    setProgress(0);
    lastFrame.current = null;
  }, [steps.length]);

  useEffect(() => {
    if (index >= steps.length) goTo(Math.max(0, steps.length - 1));
  }, [goTo, index, steps.length]);

  useEffect(() => {
    if (!playing) {
      lastFrame.current = null;
      if (frame.current) cancelAnimationFrame(frame.current);
      return;
    }
    if (index >= steps.length - 1) {
      setPlaying(false);
      return;
    }

    const tick = (time: number) => {
      if (lastFrame.current === null) lastFrame.current = time;
      const delta = (time - lastFrame.current) / 1000;
      lastFrame.current = time;
      setProgress((current) => {
        const duration = Math.max(0.3, steps[index + 1]?.duration ?? 1.5);
        const next = current + (delta * speed) / duration;
        if (next >= 1) {
          setIndex((currentIndex) => {
            if (currentIndex >= steps.length - 2) setPlaying(false);
            return Math.min(steps.length - 1, currentIndex + 1);
          });
          lastFrame.current = time;
          return 0;
        }
        return next;
      });
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => { if (frame.current) cancelAnimationFrame(frame.current); };
  }, [index, playing, speed, steps]);

  const renderedStep = useMemo(() => {
    const from = steps[index];
    const to = steps[Math.min(index + 1, steps.length - 1)];
    if (!from || !to) return from;
    return interpolateStep(from, to, progress);
  }, [index, progress, steps]);

  return {
    index, progress, playing, speed, renderedStep,
    setSpeed,
    toggle: () => {
      if (index >= steps.length - 1) goTo(0);
      setPlaying((value) => !value);
    },
    previous: () => { setPlaying(false); goTo(index - 1); },
    next: () => { setPlaying(false); goTo(index + 1); },
    replay: () => { goTo(0); setPlaying(true); },
    goTo: (next: number) => { setPlaying(false); goTo(next); },
  };
}
